import { Schema } from "effect";

/**
 * Reseller fee types the floor + upside-split rate model applies to.
 *
 * - install: the one-time clawinstall setup charge
 * - subscription: the recurring subscription charge
 *
 * @since 1.0.0
 */
export const ResellerFeeType = Schema.Literal("install", "subscription");
export type ResellerFeeType = Schema.Schema.Type<typeof ResellerFeeType>;

/**
 * Floor + upside-share terms for a single fee type.
 *
 * `floorAmount` (cents) is our guaranteed minimum take (covers cost-to-serve +
 * base margin); `upsideShareBps` is our share, in basis points, of revenue
 * above the floor. See {@link computeResellerSplit} for the formula.
 *
 * @since 1.0.0
 */
export class RateTerm extends Schema.Class<RateTerm>("RateTerm")({
  floorAmount: Schema.Int.pipe(Schema.greaterThanOrEqualTo(0)),
  upsideShareBps: Schema.Int.pipe(Schema.between(0, 10_000)),
}) {}

/**
 * Terms keyed by fee type (each fee type optional). A scope may carry terms for
 * only `install`, only `subscription`, or both.
 *
 * @since 1.0.0
 */
export const RateTermsByFeeType = Schema.partial(
  Schema.Record({ key: ResellerFeeType, value: RateTerm }),
);
export type RateTermsByFeeType = Schema.Schema.Type<typeof RateTermsByFeeType>;

/**
 * The full per-reseller rate config stored in `reseller_profiles.rate_terms`.
 *
 * `default` applies to ALL the reseller's customers. `customerExceptions`
 * (keyed by `source_customer_id`) and `agentExceptions` (keyed by
 * `agent_installation_id`) override the default; resolution is most-specific
 * first (agent → customer → default) via {@link resolveRateTerms}. Exception
 * keys are NOT FK-enforced — a deleted target leaves a harmless dangling key
 * that simply never resolves.
 *
 * @since 1.0.0
 */
export class RateTermsConfig extends Schema.Class<RateTermsConfig>("RateTermsConfig")({
  default: RateTermsByFeeType,
  customerExceptions: Schema.Record({ key: Schema.String, value: RateTermsByFeeType }),
  agentExceptions: Schema.Record({ key: Schema.String, value: RateTermsByFeeType }),
}) {}
